import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
    property_interest: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success('Your inquiry has been submitted successfully! We will contact you soon.');
      setFormData({
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phone: '',
        message: '',
        property_interest: ''
      });
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3NzExMzE1Njl8MA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80" />
        <div className="relative z-10 text-center px-6 text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold tracking-tight mb-4">
            Get In Touch
          </h1>
          <p className="text-xl text-emerald-50">Let's discuss your dream property</p>
        </div>
      </section>

      <section className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <p className="text-stone-600 uppercase tracking-widest text-xs mb-4">CONTACT INFORMATION</p>
            <h2 className="text-4xl font-playfair font-bold text-stone-900 mb-8 tracking-tight">
              We're Here to Help
            </h2>

            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="p-3 bg-emerald-900 text-white">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-stone-900 mb-1">Email Us</p>
                  <p className="text-stone-600">info@siddhivinayakinfra.estate</p>
                  <p className="text-stone-600">support@siddhivinayakinfra.estate</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="p-3 bg-emerald-900 text-white">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-stone-900 mb-1">Call Us</p>
                  <p className="text-stone-600">+91 8625009659</p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="p-3 bg-emerald-900 text-white">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold text-stone-900 mb-1">Corporate Office</p>
                  <p className="text-stone-600">Office No. BR2-330A to 334, 3rd Floor,</p>
                  <p className="text-stone-600">Jai Ganesh Vision, Akurdi,</p>
                  <p className="text-stone-600">Pune - 411035</p>
                </div>
              </div>
            </div>

            <div className="p-8 bg-stone-100 border-l-4 border-amber-600">
              <h3 className="text-xl font-playfair font-bold text-stone-900 mb-3">Office Hours</h3>
              <p className="text-stone-700 mb-2"><strong>Monday - Sunday:</strong> 10:00 AM - 10:00 PM</p>
            </div>
          </div>

          <div>
            <div className="bg-white border border-stone-200 p-8">
              <h3 className="text-2xl font-playfair font-bold text-stone-900 mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Input
                        name="firstName"
                        placeholder="First Name *"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        data-testid="contact-firstname-input"
                        className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                    </div>
                    <div>
                      <Input
                        name="middleName"
                        placeholder="Middle Name"
                        value={formData.middleName}
                        onChange={handleChange}
                        data-testid="contact-middlename-input"
                        className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                    </div>
                    <div>
                      <Input
                        name="lastName"
                        placeholder="Last Name *"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        data-testid="contact-lastname-input"
                        className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                      />
                    </div>
                  </div>

                  <div>
                    <Input
                      type="email"
                      name="email"
                      placeholder="Your Email *"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email-input"
                      className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                    />
                  </div>

                  <div>
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Your Phone *"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="contact-phone-input"
                      className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                    />
                  </div>

                  <div>
                    <Input
                      name="property_interest"
                      placeholder="Property of Interest (Optional)"
                      value={formData.property_interest}
                      onChange={handleChange}
                      data-testid="contact-property-input"
                      className="h-12 border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900"
                    />
                  </div>

                  <div>
                    <Textarea
                      name="message"
                      placeholder="Your Message *"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      data-testid="contact-message-input"
                      className="border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="w-full bg-emerald-900 hover:bg-emerald-800 text-white px-8 py-6 uppercase tracking-widest text-xs font-bold transition-all duration-300"
                  >
                    {loading ? 'SENDING...' : (
                      <>
                        SEND MESSAGE <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;